import PropTypes from 'prop-types';
import { useEffect, useState } from 'react';
import { Link as RouterLink, useLocation } from 'react-router';
import { useSelector } from 'react-redux';
// material
import { experimentalStyled as styled } from '@mui/material/styles';
import { Box, Drawer } from '@mui/material';
// hooks
import useAuth from 'src/hooks/useAuth';
import { useSupportHubEnabled } from 'src/hooks/useSupportHubEnabled';
// components
import SidebarFooter from './SidebarFooter';
import Logo from 'src/components/Logo';
import Scrollbar from 'src/components/Scrollbar';
import NavSection from 'src/components/NavSection';
import { MHidden } from 'src/components/@material-extend/index';
import tenantSidebarConfig from './TenantSidebarConfig';
import complianceSidebarConfig from './ComplianceSidebarConfig';
import regulatoryManagerConfig from './RegulatoryManagerConfig';
import SidebarControl from './SidebarControl';

// ----------------------------------------------------------------------

const DRAWER_WIDTH = 280;

const RootStyle = styled('div')(({ theme, width = 280 }) => ({
  position: 'relative',
  [theme.breakpoints.up('lg')]: {
    flexShrink: 0,
    width: width,
  },
}));

// ----------------------------------------------------------------------

DashboardSidebar.propTypes = {
  isOpenSidebar: PropTypes.bool,
  onCloseSidebar: PropTypes.func,
};

export default function DashboardSidebar({ isOpenSidebar, onCloseSidebar }) {
  const { pathname } = useLocation();
  const { user } = useAuth();
  const { memberType, householdMember } = useSelector((state) => state.main);
  const { isLoading } = useSelector((state) => state.main);
  const currentRoles = user.groups.map((role) => role.name.toLowerCase());
  const supportHubEnabled = useSupportHubEnabled();

  const [sidebar, setSidebar] = useState(<></>);
  const [isCompliance, setIsCompliance] = useState(false);
  const { selectedProperty } = useSelector((state) => state.property);

  useEffect(() => {
    /**
     * Set sidebar options and configuration, based on the memberType or if it's loading
     */

    let sidebarConfiguration = [];

    if (isLoading) {
      sidebarConfiguration = [];
    } else if (pathname.startsWith('/user-management')) {
      // User Management (ENG-2139) is its own top-level page and
      // intentionally doesn't use a role-specific sidebar. Without
      // this short-circuit the else-chain below falls through to the
      // "USER IS NOT AUTHORIZED" console.error because no
      // `/workbench` or `/pronto-admin` prefix matches — a spurious
      // authorization error on every page load (Bugbot LOW).
      sidebarConfiguration = [];
    } else {
      /*
       * Select the sidebar depending on the user roles
       * 1. Validate for compliance
       * 2. Validate for Pronto Administration
       * 3. Validate for resident
       */

      const isCompliance_ = Boolean(pathname.startsWith('/workbench'));
      const isProntoAdministration_ = pathname.startsWith('/pronto-admin');

      if (
        isCompliance_ &&
        (currentRoles.includes('compliance') || currentRoles.includes('owner'))
      ) {
        // ENG-4211 — the case surfaces are no longer separate nav entries: the
        // Assistant, the knowledge base and the settings live inside one hub.
        // PA-10 — the entry is named after the product, so the sidebar is told
        // whether this user has the Assistant.
        sidebarConfiguration = complianceSidebarConfig(
          supportHubEnabled,
          currentRoles.includes('support'),
          Boolean(user?.supportCasesEnabled)
        );
      } else if (
        isProntoAdministration_ &&
        (currentRoles.includes('audit') ||
          currentRoles.includes('administrator'))
      ) {
        sidebarConfiguration = regulatoryManagerConfig;
      } else if (currentRoles.includes('resident')) {
        sidebarConfiguration = tenantSidebarConfig(memberType, householdMember);
      } else {
        // Unauthorized
        console.error('USER IS NOT AUTHORIZED');
      }
    }

    setSidebar(
      <NavSection
        navConfig={sidebarConfiguration}
        user={user}
        emptyMessage="Select a property to see more options."
        sx={{ textTransform: 'none' }}
        onClose={onCloseSidebar}
      />
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoading, pathname, selectedProperty, memberType, householdMember]);
  // const { user } = useAuth();

  useEffect(() => {
    setIsCompliance(pathname.startsWith('/workbench'));
  }, [pathname]);

  const renderContent = (
    <Scrollbar
      sx={{
        height: '100%',
        '& .simplebar-content': {
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
        },
      }}
    >
      <Box sx={{ px: 2.5, py: 3 }}>
        <Box
          component={RouterLink}
          to="/"
          sx={{
            width: '100%',
            mt: 0,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-start',
            textDecoration: 'none',
            ml: 2,
          }}
        >
          <Logo isCompliance={isCompliance} />
        </Box>
      </Box>

      {sidebar}

      <Box sx={{ flexGrow: 1 }} />
    </Scrollbar>
  );

  return (
    <RootStyle width={isOpenSidebar ? DRAWER_WIDTH : 0}>
      <MHidden width="lgUp">
        <Drawer
          open={isOpenSidebar}
          onClose={onCloseSidebar}
          ModalProps={{ disableEnforceFocus: true }}
          slotProps={{
            paper: {
              sx: { width: DRAWER_WIDTH },
            },
          }}
        >
          {renderContent}
          <SidebarFooter />
        </Drawer>
      </MHidden>
      <MHidden width="lgDown">
        <Drawer
          open={isOpenSidebar}
          variant="persistent"
          slotProps={{
            paper: {
              sx: {
                width: DRAWER_WIDTH,
                backgroundColor: 'background.default',
              },
            },
          }}
        >
          {renderContent}
          <SidebarFooter />
        </Drawer>

        {renderContent && !pathname.startsWith('/user-management') && (
          <SidebarControl />
        )}
      </MHidden>
    </RootStyle>
  );
}
